import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase } from '../utils/authHeaders';
import {
  capabilitiesAllow, errorMessage, readMktScope, requestConfig, scopeKey, useMktScope,
} from './MarketplaceShared';

// Scope, decimal, id and formatting helpers are the marketplace ones: same org/site/department
// headers, same exact-decimal string arithmetic, same idempotency-key semantics.
export {
  capabilitiesAllow, decCmp, decimalProps, displayDec, errorCode, errorMessage, formatWhen, isDecimal,
  isPositiveDecimal, money, newUuid, requestConfig, scopeKey, shortId, statusLabel, useStableIds,
} from './MarketplaceShared';

export const ctlApi = axios.create({ baseURL: `${apiBase()}/api/controls` });

export const readCtlScope = readMktScope;
export const useCtlScope = useMktScope;

export function useControlsNavEnabled(token, probe = true) {
  const scope = useCtlScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (probe && scope.orgId && scope.token) {
      ctlApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(res => { if (!controller.signal.aborted && capabilitiesAllow(res?.data)) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope, probe]);
  return allowedKey === key;
}

/**
 * Request/mutation runtime for a workspace that is remounted (keyed) per scope.
 * `active()` turns false after unmount or once org/site/department in storage no longer
 * matches the scope this workspace was rendered for, so late responses are dropped.
 */
export function useCtlRuntime(scope) {
  const lifetime = useRef(new AbortController());
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);
  const active = useCallback(
    () => !lifetime.current.signal.aborted && scopeKey(readCtlScope()) === scopeKey(scope),
    [scope],
  );
  const request = useCallback(async (method, path, { body, params } = {}) => {
    const config = { ...requestConfig(scope, lifetime.current.signal) };
    if (params) config.params = params;
    const res = ['get', 'delete'].includes(method)
      ? await ctlApi[method](path, config)
      : await ctlApi[method](path, body, config);
    return res.data;
  }, [scope]);
  const run = useCallback(async (work, success) => {
    if (lock.current || !active()) return undefined;
    lock.current = true;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await work();
      if (active() && success) setNotice(success);
      return { ok: true, result };
    } catch (e) {
      if (active()) setError(errorMessage(e));
      return { ok: false, error: e };
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }, [active]);
  return { active, request, run, busy, error, notice, setError, setNotice };
}

export const asList = v => {
  if (Array.isArray(v)) return v;
  return Array.isArray(v?.items) ? v.items : [];
};

// --- presentation (ctl- namespace so Controls.css is self-contained) -------

export function Field({ label, options, children, ...props }) {
  return (
    <label className="ctl-field">
      <span>{label}</span>
      {options
        ? <select aria-label={label} {...props}>{options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select>
        : <input aria-label={label} {...props} />}
      {children}
    </label>
  );
}

export function Table({ headers, children, empty, emptyText = 'No records.', label }) {
  return (
    <div className="ctl-table-wrap" tabIndex={0} role="region" aria-label={`${label || headers[0]} records`}>
      <table>
        <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <p className="ctl-empty">{emptyText}</p>}
    </div>
  );
}

export function Messages({ error, notice }) {
  return (
    <>
      {error && <div className="ctl-error" role="alert">{error}</div>}
      {notice && <div className="ctl-notice" role="status">{notice}</div>}
    </>
  );
}
